import React, {
  HTMLAttributeAnchorTarget,
  useEffect,
  useRef,
  useState,
} from 'react';
import Link from 'components/link';
import Image from 'next/image';
import { gsap } from 'gsap';
import styles from './style.module.scss';
import Button, {
  SECONDARY_THEME,
} from 'components/control/button';

import type {
  BuiltForThisLogo,
  BuiltForThisVideo,
} from 'tenants/types/tenant';

/** The details of the call-to-action button */
export type Cta = {
  /** label for the call-to-action button */
  label: string;
  /**  link for the call-to-action button. If the link is not provided, the button will not be displayed. */
  link: string;
  /** optional target for the call-to-action button. Default is '_blank' */
  target?: HTMLAttributeAnchorTarget | undefined;
  /** optional rel for the call-to-action button. Default is 'noopener noreferrer' */
  rel?: string | undefined;
  /** optional click handler for the call-to-action button */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

export type BuiltForThisProps = {
  /** the details of the call-to-action button (optional) */
  cta?: Cta;
  /** the logo of the built for this section */
  logo: BuiltForThisLogo;
  /** the video of the built for this section */
  video: BuiltForThisVideo;
};

export default function BuiltForThis({ cta, logo, video }: BuiltForThisProps) {
  const firstTextRef = useRef<HTMLDivElement>(null);
  const secondTextRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const firstTextTween = useRef<gsap.core.Tween | null>(null);
  const secondTextTween = useRef<gsap.core.Tween | null>(null);
  const lastScrollY = useRef(0);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const [imgCountReady, setImgCountReady] = useState(0);

  useEffect(() => {
    // ensure images loaded to setup animation
    if (imgCountReady < 10) return;
    if (firstTextTween.current && secondTextTween.current) return;

    const createInfiniteScroll = (element: HTMLElement | null, direction: number) => {
      if (!element) return;

      const svgWidth = element.querySelector('img')?.getBoundingClientRect().width || 0;

      gsap.set(element, {
        x: direction > 0 ? 0 : -svgWidth,
      });

      return gsap.to(element, {
        x: direction > 0 ? -svgWidth : 0,
        duration: 20,
        repeat: -1,
        ease: 'none',
        modifiers: {
          x: gsap.utils.unitize(x => parseFloat(x) % svgWidth),
        },
      });
    };

    const firstTextRefCurrent = firstTextRef.current;
    firstTextTween.current = createInfiniteScroll(firstTextRefCurrent, -1); // Move left
    const secondTextRefCurrent = secondTextRef.current;
    secondTextTween.current = createInfiniteScroll(secondTextRefCurrent, 1); // Move right

    return () => {
      if (firstTextTween.current) gsap.killTweensOf(firstTextTween.current);
      if (secondTextTween.current) gsap.killTweensOf(secondTextTween.current);
    };
  }, [imgCountReady]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDiff = currentScrollY - lastScrollY.current;
      const scrollSpeed = Math.abs(scrollDiff) * 0.05;

      // Clear existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      // Update animation speed based on scroll direction and speed
      if (firstTextTween.current && secondTextTween.current) {
        const baseDuration = 20;
        const speedMultiplier = scrollDiff > 0 ? 1 - scrollSpeed : 1 + scrollSpeed;
        const newDuration = Math.max(5, Math.min(35, baseDuration * speedMultiplier));

        // Store current progress
        const firstProgress = firstTextTween.current.progress();
        const secondProgress = secondTextTween.current.progress();

        // Update duration while maintaining progress
        firstTextTween.current.duration(newDuration).progress(firstProgress);
        secondTextTween.current.duration(newDuration).progress(secondProgress);
      }

      lastScrollY.current = currentScrollY;

      // Reset to default speed after scrolling stops
      scrollTimeout.current = setTimeout(() => {
        if (firstTextTween.current && secondTextTween.current) {
          // Store current progress before resetting
          const firstProgress = firstTextTween.current.progress();
          const secondProgress = secondTextTween.current.progress();

          // Reset duration while maintaining progress
          firstTextTween.current.duration(20).progress(firstProgress);
          secondTextTween.current.duration(20).progress(secondProgress);
        }
      }, 150);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, []);

  const handleVideoClick = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (!document.fullscreenElement) {
        await video.requestFullscreen();
        video.muted = false;
      }
    } catch (error) {
      console.error('Error entering fullscreen:', error);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;

      if (videoRef.current) {
        videoRef.current.muted = !isCurrentlyFullscreen;
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const generateRepeatedLogo = (count: number, src: string, alt: string) => {
    return Array(count).fill(null).map((_, index) => (
      <div key={index} className={styles.logoWrapper}>
        <Image src={src} alt={alt} fill onLoad={() => setImgCountReady(prev => ++prev)} />
      </div>
    ));
  };

  return (
    <div className={styles.component}>
      <div className={styles.videoWrapper}>
        <div className={styles.videoContainer}>
          <video ref={videoRef} autoPlay muted loop playsInline onClick={handleVideoClick} >
            <source src={video.src} type={video.type} />
          </video>
        </div>
        <div className={styles.scrollingText} ref={firstTextRef}>{generateRepeatedLogo(5, logo.src, logo.alt)}</div>
      </div>
      <div className={styles.content}>
        <div className={styles.textContainer}>
          <div className={styles.scrollingText} ref={secondTextRef}>
            {generateRepeatedLogo(5, logo.src, logo.alt)}
          </div>
        </div>
        {cta && (
          <Link onClick={cta.onClick} href={cta.link} target={cta.target} rel={cta.rel}>
            <Button label={cta.label} theme={SECONDARY_THEME} className={styles.ctaButton} />
          </Link>
        )}
      </div>
    </div>
  );
}
